import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable, signal, inject } from "@angular/core";
import { tap } from "rxjs/operators";
import { LoginFormData, LoginResponse, RegistrationFormData, ResetPasswordFormData } from "../../interfaces/auth.interfaces";
import { RequestVerificationCodeResponse, VerifyVerificationCodeResponse } from "../../interfaces/verification.interfaces";
import { BASE_URL } from "../base-url";
import { TokenService } from "../token.service";
import { SKIP_GLOBAL_ERROR } from "../../interceptors/error.interceptor";
import { NotificationsService } from '../../services/notifications/notifications.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    userRegisteredSuccessfully = signal(false);
    passwordResetSuccessfully = signal(false);
    private tokenService = inject(TokenService);
    private notificationsService = inject(NotificationsService);

    constructor(private http: HttpClient) { }

    login(loginData: LoginFormData) {
        return this.http.post<LoginResponse>(`${BASE_URL}account/login`, loginData, {
            context: new HttpContext().set(SKIP_GLOBAL_ERROR, true)
        }).pipe(
            tap((response: LoginResponse) => {
                if (response.accessToken && response.refreshToken && response.roleId) {
                    this.tokenService.saveTokens(
                        response.accessToken,
                        response.refreshToken,
                        response.roleId
                    );
                    this.notificationsService.fetchNotificationCounts();
                }
            })
        );
    }

    checkEmailAvailability(email: string) {
        return this.http.post(`${BASE_URL}account/email-exists`, { email });
    }

    checkUsernameAvailability(username: string) {
        return this.http.post<boolean>(`${BASE_URL}account/username-exists`, { username });
    }

    register(registrationData: RegistrationFormData) {
        return this.http.post(`${BASE_URL}account/register`, registrationData, {
            context: new HttpContext().set(SKIP_GLOBAL_ERROR, true)
        });
    }

    confirmEmail(confirmationCode: string) {
        return this.http.get(`${BASE_URL}account/confirm-email?confirmationCode=${confirmationCode}`);
    }

    requestPasswordReset(contact: string) {
        return this.http.post<RequestVerificationCodeResponse>(`${BASE_URL}account/password/reset`, { contact }, {
            context: new HttpContext().set(SKIP_GLOBAL_ERROR, true)
        });
    }

    confirmPasswordReset(contact: string, code: string) {
        return this.http.post<VerifyVerificationCodeResponse>(`${BASE_URL}account/password/reset/confirm`, { contact, code }, {
            context: new HttpContext().set(SKIP_GLOBAL_ERROR, true)
        });
    }

    resetPassword(resetPasswordFormData: ResetPasswordFormData) {
        return this.http.post(`${BASE_URL}account/password/confirmation`, resetPasswordFormData, {
            context: new HttpContext().set(SKIP_GLOBAL_ERROR, true)
        });
    }

    refreshToken() {
        const accessToken = this.tokenService.getAccessToken();
        const refreshToken = this.tokenService.getRefreshToken();

        if (!accessToken || !refreshToken) {
            throw new Error('No tokens available for refresh');
        }

        const refreshData = {
            accessToken,
            refreshToken
        };

        return this.http.post<LoginResponse>(`${BASE_URL}account/refresh`, refreshData).pipe(
            tap((response: LoginResponse) => {
                if (response.accessToken && response.refreshToken && response.roleId) {
                    this.tokenService.saveTokens(
                        response.accessToken,
                        response.refreshToken,
                        response.roleId
                    );
                }
            })
        );
    }

    getCurrentUser() {
        return this.http.get<any>(`${BASE_URL}account/profile`);
    }

    updateProfile(profileData: any) {
        return this.http.put(`${BASE_URL}account/profile`, profileData);
    }
}
